import React, { useState, useEffect } from 'react';
import { LicensePlate, SortOption } from '../types';
import PlateCard from './PlateCard';
import SortControls from './SortControls';
import { PlateService } from '../services/plateService';
import { VoteService } from '../services/voteService';
import UserService from '../services/userService';

const PlateGrid: React.FC = () => {
  const [plates, setPlates] = useState<LicensePlate[]>([]);
  const [plateCount, setPlateCount] = useState<number | null>(null);
  const [loading, setLoading] = useState(true);
  const [sortBy, setSortBy] = useState<SortOption>('newest');
  const [votedPlates, setVotedPlates] = useState<Set<string>>(new Set());


  const mockPlates: LicensePlate[] = [
    {
      id: '1',
      imageUrl: 'https://images.unsplash.com/photo-1558618666-fcd25c85cd64?w=400&h=300&fit=crop',
      state: 'CA',
      uploadDate: new Date('2024-01-15') as any,
      votes: { upvotes: 15, downvotes: 3, total: 12 },
      voteHistory: [],
      emojiReactions: []
    },
    {
      id: '2',
      imageUrl: 'https://images.unsplash.com/photo-1558618666-fcd25c85cd64?w=400&h=300&fit=crop',
      state: 'NY',
      uploadDate: new Date('2024-01-14') as any,
      votes: { upvotes: 8, downvotes: 1, total: 7 },
      voteHistory: [],
      emojiReactions: []
    },
    {
      id: '3',
      imageUrl: 'https://images.unsplash.com/photo-1558618666-fcd25c85cd64?w=400&h=300&fit=crop',
      state: 'TX',
      uploadDate: new Date('2024-01-13') as any,
      votes: { upvotes: 22, downvotes: 5, total: 17 },
      voteHistory: [],
      emojiReactions: []
    },
    {
      id: '4',
      imageUrl: 'https://images.unsplash.com/photo-1558618666-fcd25c85cd64?w=400&h=300&fit=crop',
      state: 'FL',
      uploadDate: new Date('2024-01-12') as any,
      votes: { upvotes: 6, downvotes: 8, total: -2 },
      voteHistory: [],
      emojiReactions: []
    },
    {
      id: '5',
      imageUrl: 'https://images.unsplash.com/photo-1558618666-fcd25c85cd64?w=400&h=300&fit=crop',
      state: 'WA',
      uploadDate: new Date('2024-01-11') as any,
      votes: { upvotes: 12, downvotes: 2, total: 10 },
      voteHistory: [],
      emojiReactions: []
    },
    {
      id: '6',
      imageUrl: 'https://images.unsplash.com/photo-1558618666-fcd25c85cd64?w=400&h=300&fit=crop',
      state: 'OR',
      uploadDate: new Date('2024-01-10') as any,
      votes: { upvotes: 4, downvotes: 6, total: -2 },
      voteHistory: [],
      emojiReactions: []
    }
  ];

  useEffect(() => {
    loadPlates();
  }, [sortBy]); // eslint-disable-line react-hooks/exhaustive-deps

  const loadPlates = async () => {
    setLoading(true);
    try {
      const [fetchedPlates, count] = await Promise.all([
        PlateService.getAllPlates(sortBy),
        PlateService.getPlateCount()
      ]);
      setPlates(fetchedPlates);
      setPlateCount(count);
    } catch (error) {
      console.error('Error loading plates:', error);
      setPlates(mockPlates);
      setPlateCount(mockPlates.length);
    } finally {
      setLoading(false);
    }
  };

  const handleVote = async (plateId: string, voteType: 'upvote' | 'downvote') => {
    if (VoteService.hasUserVoted(plateId)) return;

    setPlates(prevPlates => 
      prevPlates.map(plate => {
        if (plate.id === plateId) {
          const newVotes = { ...plate.votes };
          if (voteType === 'upvote') {
            newVotes.upvotes += 1;
            newVotes.total += 1;
          } else {
            newVotes.downvotes += 1;
            newVotes.total -= 1;
          }
          return { ...plate, votes: newVotes };
        }
        return plate;
      })
    );
    
    setVotedPlates(prev => new Set([...prev, plateId]));

    try {
      const result = await VoteService.submitVote(plateId, voteType);
      if (!result.success) loadPlates();
    } catch (error) {
      console.error('Error submitting vote:', error);
      loadPlates();
    }
  };

  const handleEmojiReaction = async (plateId: string, emoji: string) => {
    try {
      const userId = await UserService.getUserId();
      const today = new Date().toISOString().split('T')[0];
      
      setPlates(prevPlates => 
        prevPlates.map(plate => {
          if (plate.id === plateId) {
            const existingReactionIndex = plate.emojiReactions.findIndex(r => r.emoji === emoji);
            
            if (existingReactionIndex >= 0) {
              const existingReaction = plate.emojiReactions[existingReactionIndex];
              const userReactions = existingReaction.userReactions || {};
              if (userReactions[userId] === today) return plate;
              
              const updatedReactions = [...plate.emojiReactions];
              updatedReactions[existingReactionIndex] = {
                ...updatedReactions[existingReactionIndex],
                count: updatedReactions[existingReactionIndex].count + 1,
                users: [...updatedReactions[existingReactionIndex].users, userId],
                userReactions: { ...updatedReactions[existingReactionIndex].userReactions, [userId]: today }
              };
              return { ...plate, emojiReactions: updatedReactions };
            } else {
              return {
                ...plate,
                emojiReactions: [...plate.emojiReactions, { emoji, count: 1, users: [userId], userReactions: { [userId]: today } }]
              };
            }
          }
          return plate;
        })
      );
      
      await PlateService.addEmojiReaction(plateId, emoji, userId);
    } catch (error) {
      console.error('Error adding emoji reaction:', error);
      loadPlates();
    }
  };

  const sortedPlates = [...plates].sort((a, b) => {
    switch (sortBy) {
      case 'newest':
        const bDate = (b.uploadDate as any).toDate ? (b.uploadDate as any).toDate() : b.uploadDate as Date;
        const aDate = (a.uploadDate as any).toDate ? (a.uploadDate as any).toDate() : a.uploadDate as Date;
        return bDate.getTime() - aDate.getTime();
      case 'oldest':
        const aDateOld = (a.uploadDate as any).toDate ? (a.uploadDate as any).toDate() : a.uploadDate as Date;
        const bDateOld = (b.uploadDate as any).toDate ? (b.uploadDate as any).toDate() : b.uploadDate as Date;
        return aDateOld.getTime() - bDateOld.getTime();
      case 'highest-rated':
        return b.votes.total - a.votes.total;
      case 'lowest-rated':
        return a.votes.total - b.votes.total;
      default:
        return 0;
    }
  });

  if (loading) {
    return (
      <div className="flex justify-center items-center py-10">
        <div className="flex flex-col items-center gap-4">
          <div className="animate-spin rounded-full h-12 w-12 border-2 border-primary-200 dark:border-primary-800 border-t-primary-500 dark:border-t-primary-400"></div>
          <p className="text-sm text-gray-500 dark:text-slate-400">Loading plates...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col items-center">
      <SortControls sortBy={sortBy} onSortChange={setSortBy} plateCount={plateCount} />
      
      <div className="w-full max-w-2xl mt-4 space-y-4">
        {sortedPlates.map(plate => (
          <PlateCard
            key={plate.id}
            plate={plate}
            onVote={handleVote}
            hasVoted={votedPlates.has(plate.id)}
            onEmojiReaction={handleEmojiReaction}
          />
        ))}
      </div>

      {plates.length === 0 && (
        <div className="text-center py-10 w-full max-w-2xl">
          <div className="w-20 h-20 mx-auto bg-gray-100 dark:bg-slate-700/50 rounded-2xl flex items-center justify-center mb-3">
            <svg className="w-12 h-12 text-gray-400 dark:text-slate-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
            </svg>
          </div>
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-1">No plates yet</h3>
          <p className="text-gray-600 dark:text-slate-400">Be the first to upload a custom license plate!</p>
        </div>
      )}
    </div>
  );
};

export default PlateGrid;
